#!/usr/bin/env node
// stars.mjs — the navigational stars, reduced to apparent place by our own code.
//
// FOUNDER, 2026-08-02: "now do the moon and stars too."
//
// A star is easier than the Moon and less forgiving than it looks. Its catalogue position is fixed at
// J2000, and getting from there to tonight means four corrections, each of which a candidate is examined
// on somewhere: PROPER MOTION (the star's own travel — Rigil Kentaurus moves 1.59' in the 26 years since
// J2000, sixteen times the almanac's tabulation, so this is not optional), PRECESSION (the earth's axis
// swinging round in 26,000 years), NUTATION (its small nod), and ABERRATION (the earth's own velocity
// tilting the incoming light).
//
// WHAT IS OURS AND WHAT IS NOT. The positions and proper motions are catalogue FACTS — Hipparcos and the
// Yale Bright Star Catalogue, both public — extracted once into navigational-stars.json with the source
// named. The REDUCTION below is ours. We claim no ownership of where the stars are; nobody can.
// Imported as a MODULE, not read off the disk. This file has to run in a browser now — a cadet at
// sea has no server to ask — and `node:fs` is the one thing that cannot follow it there. The
// catalogue stays a .json so it is still the citable artefact it was; only the way it is loaded
// changed. Node 20.19 and Vite both take the import attribute; verified before relying on it.
import CAT from './navigational-stars.json' with { type: 'json' };

const D = Math.PI / 180;
const sin = (d) => Math.sin(d * D), cos = (d) => Math.cos(d * D);
// tan IN DEGREES. Its absence was the whole bug: sin and cos had degree-taking helpers and tan did not,
// so three call sites reached for Math.tan() on a value in degrees. Math.tan(23.44) is 8.0; the obliquity's
// tangent is 0.434 — an eighteen-fold error on that term, and the reason star declinations were out by 1.1'.
// A missing helper beside two present ones is an invitation to exactly this mistake.
const tan = (d) => Math.tan(d * D);
const norm360 = (a) => ((a % 360) + 360) % 360;

/** Precession angles from J2000 to date (IAU 1976), in degrees. */
function precessionAngles(T) {
  const s = 1 / 3600;
  return {
    zeta:  s * (2306.2181 * T + 0.30188 * T * T + 0.017998 * T ** 3),
    z:     s * (2306.2181 * T + 1.09468 * T * T + 0.018203 * T ** 3),
    theta: s * (2004.3109 * T - 0.42665 * T * T - 0.041833 * T ** 3),
  };
}

/**
 * Apparent place of a catalogue star.
 * @param star   {ra_j2000, dec_j2000, pmra_mas, pmdec_mas}
 * @param ctx    {T, nutation:{dpsi,deps}, eps, sunLambda, earthEcc, sunPeri}
 */
export function apparentPlace(star, ctx) {
  const { T, nut, eps, sunLambda, ecc, peri } = ctx;
  // 1 — PROPER MOTION. pmra is mu-alpha*cos(delta) in mas/yr (the Hipparcos convention), so it is
  // divided by cos(dec) to become a change in right ascension itself.
  const years = T * 100;
  const dec0 = star.dec_j2000;
  let ra = star.ra_j2000 + (star.pmra_mas / 3600000) * years / Math.max(1e-9, cos(dec0));
  let dec = dec0 + (star.pmdec_mas / 3600000) * years;

  // 2 — PRECESSION, by the rigorous rotation rather than the approximate formulae
  const { zeta, z, theta } = precessionAngles(T);
  const A = cos(dec) * sin(ra + zeta);
  const B = cos(theta) * cos(dec) * cos(ra + zeta) - sin(theta) * sin(dec);
  const C = sin(theta) * cos(dec) * cos(ra + zeta) + cos(theta) * sin(dec);
  ra = norm360(Math.atan2(A, B) / D + z);
  dec = Math.asin(Math.max(-1, Math.min(1, C))) / D;

  // 3 — NUTATION in right ascension and declination (the standard first-order form)
  const dp = nut.dpsi / 3600, de = nut.deps / 3600;
  const dra = (cos(eps) + sin(eps) * sin(ra) * tan(dec)) * dp - (cos(ra) * tan(dec)) * de;
  const ddec = (sin(eps) * cos(ra)) * dp + sin(ra) * de;

  // 4 — ANNUAL ABERRATION. k is the constant of aberration; the earth's velocity has a circular part
  // (the Sun's longitude) and an elliptic part (the orbit's eccentricity and perihelion).
  const k = 20.49552 / 3600;
  const ara = -k * (cos(ra) * cos(sunLambda) * cos(eps) + sin(ra) * sin(sunLambda)) / cos(dec)
            + ecc * k * (cos(ra) * cos(peri) * cos(eps) + sin(ra) * sin(peri)) / cos(dec);
  const adec = -k * (cos(sunLambda) * cos(eps) * (tan(eps) * cos(dec) - sin(ra) * sin(dec))
                     + cos(ra) * sin(dec) * sin(sunLambda))
             + ecc * k * (cos(peri) * cos(eps) * (tan(eps) * cos(dec) - sin(ra) * sin(dec))
                     + cos(ra) * sin(dec) * sin(peri));

  return { ra: norm360(ra + dra + ara), dec: dec + ddec + adec };
}

export const catalogue = () => CAT.stars;
export const catalogueSource = () => CAT.source;
